import * as Hooks from 'preact/hooks';
import * as Selectors from '../../store/selectors';
import { useSelector } from '../../store/redux';
import { Api, Fixtuur } from '@fixtuur/backpack';
import { useGetConfigurationAllOptions } from './useGetOptions';

// All possible selections, taking into account priorities defined in the config definition.
// Specifically intended to be passed to gavel as the last part of the proposal object when
// retrieving selections as we need to guarantee we always have product selections for every part
// for most of our app to function
export const useConfigurationFallbacks = () => {
    const inConfiguration = useSelector(Selectors.Initialisation.inConfiguration);
    const currentConfigurator = useSelector(Selectors.Initialisation.currentConfiguratorDefinition);
    const allOptions = useGetConfigurationAllOptions();

    const fallbacks = Hooks.useMemo(() => {
        if (!inConfiguration) {
            // fallbacks only make sense for configuration requests
            return [];
        }
        if (!allOptions || !currentConfigurator) {
            // options haven't yet generated, stay in the loading state.
            return null;
        }

        return buildConfigurationFallbacks(currentConfigurator, allOptions);
    }, [allOptions, currentConfigurator]);

    return { fallbacks, isFallbacksLoading: fallbacks === null };
};

export function buildConfigurationFallbacks(
    currentConfigurator: Api.ConfiguratorDefinition.ConfiguratorDefinition,
    options: Fixtuur.Configuration.Options.Options,
): Fixtuur.Configuration.Selections.Selections[] {
    return Object.entries(currentConfigurator.propertyPriorities)
        .flatMap(([property, values]) =>
            values.map((value): [string, Fixtuur.Configuration.Option.Value] => [property, value]),
        )
        .concat(
            Object.entries(options).flatMap(([property, values]) =>
                values.map(
                    (
                        value: Fixtuur.Configuration.Option.Value,
                    ): [string, Fixtuur.Configuration.Option.Value] => [property, value],
                ),
            ),
        )
        .map(([property, value]) => Fixtuur.Configuration.Selections.create({ [property]: value }));
}
